// components/BookCover.tsx — templated SVG book cover.
//
// Reads the scenario's theme tokens from SCENARIO_PRESENTATIONS so each
// scenario's books look visually distinct without needing per-cover art.
// Pure SVG — no canvas, no images, no external deps.
//
// Layout (200×280):
//   - Background panel painted in the scenario's `paper` colour with the
//     scenario's `accent` framing the spine and a decorative sigil at the
//     bottom-centre.
//   - Title (hero font, large) wrapped onto up to 3 lines.
//   - "by <author>" below the title.
//   - Scenario name as a small footer label, accent colour.
//   - Optional status badge in the top-right corner ("In progress · N").
//
// The sigil is procedurally generated per (scenarioId, storyId) so two
// books in the same scenario don't carry the same mark. Pure mulberry32
// off a hash of `${scenarioId}:${storyId}` — no randomness at render time.

import type { CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { SCENARIO_PRESENTATIONS } from "../scenarios/presentation";

export interface BookCoverProps {
  scenarioId: string;
  title: string;
  authorName: string;
  /** "active" | "finished" | "paused"; "active" shows "In progress". */
  storyStatus?: "active" | "finished" | "paused" | null;
  /** Number of chapters; if > 0 and active, shown in the corner badge. */
  chapterCount?: number;
  /** Used to seed the sigil so different stories in the same scenario differ. */
  storyId: string;
  /** Override CSS (e.g. cursor: pointer when clickable). */
  style?: CSSProperties;
  width?: number;
  height?: number;
  /** Optional click handler — when set, the cover renders cursor: pointer. */
  onClick?: () => void;
}

const DEFAULT_WIDTH = 200;
const DEFAULT_HEIGHT = 280;

// ── Deterministic PRNG (mulberry32) seeded from a string ──────────────

function hashString(s: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  return h >>> 0;
}

function mulberry32(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// Wrap a single line of text onto up to maxLines lines of ~width chars.
// Doesn't measure pixels — uses a character-count heuristic that's good
// enough for the hand-tuned SVG layout (the font is rendered in a fixed
// size + family so the visual line width is stable).
function wrapTitle(
  title: string,
  charsPerLine: number,
  maxLines: number,
): string[] {
  const words = title.split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let current = "";
  for (const word of words) {
    if (lines.length >= maxLines) break;
    const tentative = current ? `${current} ${word}` : word;
    if (tentative.length <= charsPerLine) {
      current = tentative;
    } else {
      if (current) lines.push(current);
      current = word;
    }
  }
  if (current && lines.length < maxLines) lines.push(current);
  // Truncate the final line with an ellipsis if there's leftover.
  const consumedChars = lines.join(" ").length;
  if (consumedChars < title.length && lines.length === maxLines) {
    const last = lines[maxLines - 1]!;
    lines[maxLines - 1] = last.slice(0, Math.max(0, charsPerLine - 1)).trimEnd() + "…";
  }
  return lines;
}

// Pick one of three sigil shapes per book, parameterised by the seeded RNG.
// Each is a small ornament that sits in the lower third of the cover.
function renderSigil(
  rng: () => number,
  accent: string,
  cx: number,
  cy: number,
  size: number,
): JSX.Element {
  const shape = Math.floor(rng() * 3); // 0..2
  if (shape === 0) {
    // Diamond rosette: a central rotated square with four small diamonds at
    // the cardinals.
    const half = size / 2;
    const quart = size / 4;
    return (
      <g
        transform={`translate(${cx}, ${cy}) rotate(${rng() * 45})`}
        opacity={0.75}
      >
        <rect
          x={-quart}
          y={-quart}
          width={half}
          height={half}
          transform="rotate(45)"
          fill="none"
          stroke={accent}
          strokeWidth={1.5}
        />
        {[0, 90, 180, 270].map((deg) => (
          <rect
            key={deg}
            x={-quart * 0.4}
            y={-half - quart * 0.4}
            width={quart * 0.8}
            height={quart * 0.8}
            transform={`rotate(${deg}) rotate(45)`}
            fill={accent}
          />
        ))}
      </g>
    );
  }
  if (shape === 1) {
    // Triple-arc: three nested half-arcs evoking a sunrise / portal.
    const r1 = size * 0.45;
    const r2 = size * 0.32;
    const r3 = size * 0.18;
    return (
      <g transform={`translate(${cx}, ${cy + size * 0.2})`} opacity={0.75}>
        <path
          d={`M ${-r1} 0 A ${r1} ${r1} 0 0 1 ${r1} 0`}
          fill="none"
          stroke={accent}
          strokeWidth={1.5}
        />
        <path
          d={`M ${-r2} 0 A ${r2} ${r2} 0 0 1 ${r2} 0`}
          fill="none"
          stroke={accent}
          strokeWidth={1.5}
        />
        <path
          d={`M ${-r3} 0 A ${r3} ${r3} 0 0 1 ${r3} 0`}
          fill="none"
          stroke={accent}
          strokeWidth={1.5}
        />
        <circle cx={0} cy={0} r={2} fill={accent} />
      </g>
    );
  }
  // shape === 2 — eight-pointed star.
  const points: string[] = [];
  const outer = size * 0.45;
  const inner = size * 0.22;
  for (let i = 0; i < 16; i++) {
    const angle = (i * Math.PI) / 8 - Math.PI / 2;
    const r = i % 2 === 0 ? outer : inner;
    points.push(`${(Math.cos(angle) * r).toFixed(2)},${(Math.sin(angle) * r).toFixed(2)}`);
  }
  return (
    <g transform={`translate(${cx}, ${cy})`} opacity={0.75}>
      <polygon
        points={points.join(" ")}
        fill="none"
        stroke={accent}
        strokeWidth={1.5}
      />
      <circle cx={0} cy={0} r={2.5} fill={accent} />
    </g>
  );
}

export default function BookCover({
  scenarioId,
  title,
  authorName,
  storyStatus = null,
  chapterCount = 0,
  storyId,
  style,
  width = DEFAULT_WIDTH,
  height = DEFAULT_HEIGHT,
  onClick,
}: BookCoverProps): JSX.Element {
  const { t } = useTranslation(["bookshelf", "scenarios"]);
  const presentation = SCENARIO_PRESENTATIONS[scenarioId];
  // Defensive fallbacks for an unknown scenario — happens only if the
  // server adds a scenario before the web bundle updates.
  const theme = presentation?.theme ?? {
    bgGradient:
      "radial-gradient(ellipse at top, #1a1410 0%, #0d0907 60%, #050302 100%)",
    accent: "#d4a574",
    accentMuted: "rgba(212,165,116,0.45)",
    ink: "#e8d9b8",
    inkMuted: "#8a7355",
    paper: "rgba(20,14,10,0.55)",
    paperEdge: "rgba(212,165,116,0.18)",
    fontFamily:
      "'Cormorant Garamond', 'EB Garamond', 'Iowan Old Style', 'Palatino Linotype', Palatino, Georgia, serif",
  };
  const scenarioName = t(`scenarios:${scenarioId}.pillLabel`, {
    defaultValue: presentation?.pillLabel ?? scenarioId,
  });

  const rng = mulberry32(hashString(`${scenarioId}:${storyId}`));

  // Title wrapping
  const titleLines = wrapTitle(title || t("bookshelf:cover.untitled"), 16, 3);
  const titleLineHeight = 24;
  const titleStartY = 70;

  // Solid background colour derived from `paper`. We can't put a CSS
  // gradient inside an SVG fill, so use the accent-muted as a fallback
  // panel colour. The result reads as a flat painted cover, which is
  // exactly what we want for a templated book.
  const panelFill = "#0e0a08";
  const innerEdge = theme.accentMuted;

  const cursor = onClick ? "pointer" : "default";

  const showBadge =
    typeof chapterCount === "number" &&
    chapterCount > 0 &&
    storyStatus === "active";

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={width}
      height={height}
      viewBox={`0 0 ${DEFAULT_WIDTH} ${DEFAULT_HEIGHT}`}
      role="img"
      aria-label={t("bookshelf:cover.ariaLabel", { title, scenario: scenarioName })}
      style={{
        display: "block",
        cursor,
        userSelect: "none",
        ...(style ?? {}),
      }}
      onClick={onClick}
    >
      {/* Outer panel */}
      <rect
        x={0}
        y={0}
        width={DEFAULT_WIDTH}
        height={DEFAULT_HEIGHT}
        fill={panelFill}
      />
      {/* Inner frame */}
      <rect
        x={10}
        y={10}
        width={DEFAULT_WIDTH - 20}
        height={DEFAULT_HEIGHT - 20}
        fill="none"
        stroke={innerEdge}
        strokeWidth={1}
      />
      {/* Spine accent (left edge band) */}
      <rect
        x={0}
        y={0}
        width={8}
        height={DEFAULT_HEIGHT}
        fill={theme.accent}
        opacity={0.45}
      />

      {/* Title block */}
      <g
        fill={theme.accent}
        fontFamily={theme.fontFamily}
        textAnchor="middle"
      >
        {titleLines.map((line, i) => (
          <text
            key={i}
            x={DEFAULT_WIDTH / 2}
            y={titleStartY + i * titleLineHeight}
            fontSize={20}
            fontWeight={500}
          >
            {line}
          </text>
        ))}
      </g>

      {/* Author */}
      <text
        x={DEFAULT_WIDTH / 2}
        y={titleStartY + titleLines.length * titleLineHeight + 14}
        fill={theme.ink}
        opacity={0.85}
        fontFamily={theme.fontFamily}
        fontStyle="italic"
        fontSize={11}
        textAnchor="middle"
      >
        {t("bookshelf:cover.by", {
          author: authorName || t("bookshelf:cover.unnamedDreamer"),
        })}
      </text>

      {/* Sigil */}
      {renderSigil(rng, theme.accent, DEFAULT_WIDTH / 2, 200, 44)}

      {/* Scenario footer label */}
      <text
        x={DEFAULT_WIDTH / 2}
        y={DEFAULT_HEIGHT - 24}
        fill={theme.accent}
        opacity={0.75}
        fontFamily={theme.fontFamily}
        fontSize={10}
        letterSpacing="0.18em"
        textAnchor="middle"
      >
        {scenarioName.toUpperCase()}
      </text>

      {/* Status badge (top-right corner) */}
      {showBadge && (
        <g>
          <rect
            x={DEFAULT_WIDTH - 86}
            y={20}
            width={66}
            height={20}
            rx={2}
            fill={theme.accent}
            opacity={0.18}
          />
          <text
            x={DEFAULT_WIDTH - 53}
            y={34}
            fill={theme.accent}
            opacity={0.95}
            fontFamily="'JetBrains Mono', monospace"
            fontSize={9}
            textAnchor="middle"
          >
            {t("bookshelf:cover.chLive", { count: chapterCount })}
          </text>
        </g>
      )}
      {storyStatus === "finished" && (
        <g>
          <rect
            x={DEFAULT_WIDTH - 86}
            y={20}
            width={66}
            height={20}
            rx={2}
            fill={theme.accent}
            opacity={0.18}
          />
          <text
            x={DEFAULT_WIDTH - 53}
            y={34}
            fill={theme.accent}
            opacity={0.95}
            fontFamily="'JetBrains Mono', monospace"
            fontSize={9}
            textAnchor="middle"
          >
            {t("bookshelf:cover.complete")}
          </text>
        </g>
      )}
    </svg>
  );
}
